/**
 * MagicSystem — Player spell casting (NOT IN MINECRAFT).
 *
 * 6 spells with hotkeys, mana system, cooldowns.
 * Spells unlock as you level up (XP level 1, 3, 5, 8, 12, 18).
 *
 * Hotkeys (when magic UI visible): Z, X, C - held with Ctrl avoids zoom
 * Simplified: magic bar at bottom shows 6 slots, click or press 0-6 keys.
 */

import * as THREE from 'three';

const SPELLS = {
  fireball: {
    id: 'fireball', name: 'Bola de Fuego', icon: '🔥',
    manaCost: 20, cooldown: 2, unlockLevel: 1,
    desc: 'Lanza una bola de fuego. Daño: 6',
    key: 'Digit1',
  },
  heal: {
    id: 'heal', name: 'Curar', icon: '💚',
    manaCost: 25, cooldown: 5, unlockLevel: 3,
    desc: 'Restaura 6 HP al instante',
    key: 'Digit2',
  },
  teleport: {
    id: 'teleport', name: 'Teletransporte', icon: '⚡',
    manaCost: 35, cooldown: 8, unlockLevel: 5,
    desc: 'Teleporta 15 bloques adelante',
    key: 'Digit3',
  },
  shield: {
    id: 'shield', name: 'Escudo Arcano', icon: '🛡️',
    manaCost: 40, cooldown: 12, unlockLevel: 8,
    desc: 'Inmune al daño por 5 segundos',
    key: 'Digit4',
  },
  lightning: {
    id: 'lightning', name: 'Rayo', icon: '⚡',
    manaCost: 50, cooldown: 10, unlockLevel: 12,
    desc: 'Rayo que mata al mob donde miras',
    key: 'Digit5',
  },
  freeze: {
    id: 'freeze', name: 'Congelar Tiempo', icon: '❄️',
    manaCost: 80, cooldown: 30, unlockLevel: 18,
    desc: 'Congela a todos los mobs por 6 segundos',
    key: 'Digit6',
  },
};

export class MagicSystem {
  constructor() {
    this.maxMana = 100;
    this.mana = this.maxMana;
    this.manaRegen = 2; // per second
    this.cooldowns = new Map(); // spellId -> remaining seconds
    this.shieldActive = false;
    this._shieldTimer = 0;
    this.frozenMobs = false;
    this._frozenTimer = 0;

    // External refs set by main.js
    this.player = null;
    this.camera = null;
    this.world = null;
    this.health = null;
    this.hostileMobs = null;
    this.particles = null;
    this.hud = null;
    this.scene = null;
    this.xpSystem = null;
    this.soundManager = null;

    this._buildUI();
  }

  _buildUI() {
    // Mana bar at top-right (above the raid bar area)
    this._manaBar = document.createElement('div');
    this._manaBar.style.cssText = `
      position:fixed; top:10px; left:50%; transform:translateX(-50%);
      width:200px; height:18px; background:rgba(0,0,0,0.6);
      border:2px solid #4444FF; font-family:'Courier New',monospace;
      font-size:11px; color:#FFF; z-index:150; padding:2px;
    `;
    this._manaFill = document.createElement('div');
    this._manaFill.style.cssText = `
      height:100%; width:100%; background:linear-gradient(to right, #4488FF, #88AAFF);
      transition:width 0.2s;
    `;
    this._manaText = document.createElement('div');
    this._manaText.style.cssText = 'position:absolute;top:0;left:0;right:0;text-align:center;line-height:18px;text-shadow:1px 1px 0 #000;';
    this._manaBar.appendChild(this._manaFill);
    this._manaBar.appendChild(this._manaText);
    document.body.appendChild(this._manaBar);

    // Spell bar at bottom (above hotbar)
    this._spellBar = document.createElement('div');
    this._spellBar.style.cssText = `
      position:fixed; bottom:110px; left:50%; transform:translateX(-50%);
      display:flex; gap:4px; z-index:149;
    `;
    this._spellSlots = [];
    for (const [id, spell] of Object.entries(SPELLS)) {
      const slot = document.createElement('div');
      slot.dataset.spellId = id;
      slot.style.cssText = `
        width:40px; height:40px; background:rgba(0,0,0,0.6);
        border:2px solid #4444FF; display:flex; align-items:center;
        justify-content:center; font-size:22px; position:relative;
        font-family:'Courier New',monospace; cursor:pointer;
      `;
      slot.title = `${spell.name} — ${spell.desc}`;
      slot.innerHTML = `
        <span class="spell-icon">${spell.icon}</span>
        <div class="spell-cd" style="position:absolute;top:0;left:0;right:0;bottom:0;background:rgba(0,0,0,0.7);display:none;align-items:center;justify-content:center;color:#FFD700;font-weight:bold;font-size:14px;"></div>
        <div class="spell-key" style="position:absolute;bottom:-2px;right:2px;font-size:8px;color:#FFF;text-shadow:1px 1px 0 #000;">${spell.key.replace('Digit', '')}</div>
      `;
      slot.addEventListener('click', () => this.cast(id));
      this._spellBar.appendChild(slot);
      this._spellSlots.push({ id, slot, cdEl: slot.querySelector('.spell-cd') });
    }
    document.body.appendChild(this._spellBar);
  }

  /**
   * Try to cast a spell by id.
   */
  cast(spellId) {
    const spell = SPELLS[spellId];
    if (!spell) return false;

    // Check unlock level
    if (this.xpSystem && this.xpSystem.level < spell.unlockLevel) {
      if (this.hud) this.hud.showMessage(`${spell.name} requiere nivel ${spell.unlockLevel}`, 2);
      return false;
    }

    // Check cooldown
    if ((this.cooldowns.get(spellId) || 0) > 0) {
      if (this.hud) this.hud.showMessage(`${spell.name} en cooldown`, 1.5);
      return false;
    }

    // Check mana
    if (this.mana < spell.manaCost) {
      if (this.hud) this.hud.showMessage(`Mana insuficiente (${Math.floor(this.mana)}/${spell.manaCost})`, 1.5);
      return false;
    }

    // Cast
    this.mana -= spell.manaCost;
    this.cooldowns.set(spellId, spell.cooldown);
    this._executeSpell(spellId);
    if (this.soundManager) this.soundManager.playLevelUp?.();
    return true;
  }

  _executeSpell(spellId) {
    switch (spellId) {
      case 'fireball': return this._castFireball();
      case 'heal': return this._castHeal();
      case 'teleport': return this._castTeleport();
      case 'shield': return this._castShield();
      case 'lightning': return this._castLightning();
      case 'freeze': return this._castFreeze();
    }
  }

  _castFireball() {
    if (!this.player || !this.camera || !this.scene) return;
    // Create a fireball projectile
    const geo = new THREE.SphereGeometry(0.25, 8, 8);
    const mat = new THREE.MeshBasicMaterial({ color: 0xFF6622 });
    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.copy(this.player.position);
    mesh.position.y += 1.5;

    const dir = new THREE.Vector3();
    this.camera.getWorldDirection(dir);
    const vel = dir.clone().multiplyScalar(30);

    this.scene.add(mesh);
    if (!this._fireballs) this._fireballs = [];
    this._fireballs.push({ mesh, velocity: vel, life: 3, damage: 6 });

    if (this.hud) this.hud.showMessage('🔥 Bola de Fuego lanzada', 1);
  }

  _castHeal() {
    if (this.health) {
      this.health.heal(6);
      if (this.particles && this.player) {
        for (let i = 0; i < 10; i++) this.particles.spawnHearts?.(this.player.position);
      }
      if (this.hud) this.hud.showMessage('💚 +6 HP', 1.5);
    }
  }

  _castTeleport() {
    if (!this.player || !this.camera) return;
    const dir = new THREE.Vector3();
    this.camera.getWorldDirection(dir);
    dir.y = 0;
    dir.normalize();
    this.player.position.addScaledVector(dir, 15);
    this.player.prevPosition.copy(this.player.position);
    if (this.particles) {
      for (let i = 0; i < 20; i++) {
        this.particles.spawnEnchantSparkle?.(this.player.position);
      }
    }
    if (this.hud) this.hud.showMessage('⚡ Teletransportado', 1.5);
  }

  _castShield() {
    this.shieldActive = true;
    this._shieldTimer = 5;
    if (this.health) this.health._magicShield = true;
    if (this.hud) this.hud.showMessage('🛡️ Escudo activo (5s)', 2);
  }

  _castLightning() {
    if (!this.player || !this.camera || !this.hostileMobs) return;
    // Find closest mob in front of player within 20 blocks
    const dir = new THREE.Vector3();
    this.camera.getWorldDirection(dir);
    let bestMob = null;
    let bestDist = 20;
    for (const mob of this.hostileMobs) {
      const toMob = new THREE.Vector3().subVectors(mob.position, this.player.position);
      const dist = toMob.length();
      if (dist > bestDist) continue;
      toMob.normalize();
      if (toMob.dot(dir) < 0.5) continue; // not in front
      bestDist = dist;
      bestMob = mob;
    }
    if (bestMob) {
      bestMob.health = 0;
      if (this.particles) {
        for (let i = 0; i < 30; i++) this.particles.spawnLevelUpBurst?.(bestMob.position);
      }
      if (this.hud) this.hud.showMessage(`⚡ Rayo mata a ${bestMob.type}`, 2);
    } else {
      if (this.hud) this.hud.showMessage('No hay enemigos en la mira', 1.5);
    }
  }

  _castFreeze() {
    this.frozenMobs = true;
    this._frozenTimer = 6;
    if (this.hostileMobs) {
      for (const mob of this.hostileMobs) {
        mob._frozen = true;
        mob._frozenTimer = 6;
      }
    }
    if (this.hud) this.hud.showMessage('❄️ Tiempo congelado (6s)', 2);
  }

  update(dt) {
    // Mana regen
    if (this.mana < this.maxMana) {
      this.mana = Math.min(this.maxMana, this.mana + this.manaRegen * dt);
    }

    // Cooldowns
    for (const [id, cd] of this.cooldowns) {
      const newCd = cd - dt;
      if (newCd <= 0) this.cooldowns.delete(id);
      else this.cooldowns.set(id, newCd);
    }

    // Shield timer
    if (this.shieldActive) {
      this._shieldTimer -= dt;
      if (this._shieldTimer <= 0) {
        this.shieldActive = false;
        if (this.health) this.health._magicShield = false;
      }
    }

    // Freeze timer
    if (this.frozenMobs) {
      this._frozenTimer -= dt;
      if (this._frozenTimer <= 0) {
        this.frozenMobs = false;
        if (this.hostileMobs) {
          for (const mob of this.hostileMobs) {
            mob._frozen = false;
            mob._frozenTimer = 0;
          }
        }
      }
    }

    // Update fireball projectiles
    if (this._fireballs) {
      for (let i = this._fireballs.length - 1; i >= 0; i--) {
        const fb = this._fireballs[i];
        fb.life -= dt;
        fb.mesh.position.addScaledVector(fb.velocity, dt);

        // Check collision with mobs
        let hit = false;
        if (this.hostileMobs) {
          for (const mob of this.hostileMobs) {
            if (mob.position.distanceTo(fb.mesh.position) < 1.5) {
              mob.health -= fb.damage;
              if (this.particles) this.particles.spawnBlockBreak?.(fb.mesh.position, 0xFF4400);
              hit = true;
              break;
            }
          }
        }
        // Check collision with blocks
        if (!hit && this.world) {
          const bx = Math.floor(fb.mesh.position.x);
          const by = Math.floor(fb.mesh.position.y);
          const bz = Math.floor(fb.mesh.position.z);
          const block = this.world.getBlock(bx, by, bz);
          if (block !== 0 && block !== 7) hit = true;
        }

        if (hit || fb.life <= 0) {
          if (this.scene) this.scene.remove(fb.mesh);
          this._fireballs.splice(i, 1);
        }
      }
    }

    this._updateUI();
  }

  _updateUI() {
    // Mana bar
    const manaPct = (this.mana / this.maxMana) * 100;
    this._manaFill.style.width = manaPct + '%';
    this._manaText.textContent = `Mana: ${Math.floor(this.mana)}/${this.maxMana}`;

    // Spell slots
    for (const { id, slot, cdEl } of this._spellSlots) {
      const spell = SPELLS[id];
      const locked = this.xpSystem && this.xpSystem.level < spell.unlockLevel;
      const cd = this.cooldowns.get(id) || 0;
      const noMana = this.mana < spell.manaCost;

      if (locked) {
        slot.style.opacity = '0.3';
        slot.style.borderColor = '#555';
        cdEl.style.display = 'none';
        slot.title = `🔒 ${spell.name} (Nivel ${spell.unlockLevel})`;
      } else if (cd > 0) {
        slot.style.opacity = '0.5';
        cdEl.style.display = 'flex';
        cdEl.textContent = cd.toFixed(1) + 's';
      } else if (noMana) {
        slot.style.opacity = '0.5';
        slot.style.borderColor = '#888';
        cdEl.style.display = 'none';
        slot.title = `${spell.name} — ${spell.desc} (${spell.manaCost} mana)`;
      } else {
        slot.style.opacity = '1';
        slot.style.borderColor = '#4444FF';
        cdEl.style.display = 'none';
        slot.title = `${spell.name} — ${spell.desc} (${spell.manaCost} mana)`;
      }
    }
  }

  /** Called from main.js keydown handler. Returns true if handled. */
  handleKeyPress(code) {
    for (const [id, spell] of Object.entries(SPELLS)) {
      if (spell.key === code) {
        this.cast(id);
        return true;
      }
    }
    return false;
  }

  serialize() {
    return { mana: this.mana, cooldowns: Array.from(this.cooldowns.entries()) };
  }

  deserialize(data) {
    if (!data) return;
    this.mana = data.mana || this.maxMana;
    if (data.cooldowns) this.cooldowns = new Map(data.cooldowns);
  }
}
